import React, { useEffect, useMemo, useRef } from "react";
import type { ReportSection } from "./resumenPdf";
import { useIsMobile } from "../mobile/MobileChrome";
import { RatingCardMobile } from "../mobile/RatingCardMobile";
import { ResumenMobile, type MobileKpi } from "../mobile/ResumenMobile";
import { fetchSocialMensajes } from "../../../api";
import type { DataRecord, GoogleLocal, SocialOverviewPayload } from "../../../types";
import type { SocialPlatformFilter } from "../SocialPlatformSelector";
import { getClientBrandMeta } from "../socialBrand";
import {
  CHAN_KIND,
  CHAN_LABEL,
  CHAN_SHORT,
  channelOf,
  cleanReviewText,
  channelStats,
  computeDailySparklines,
  fmtDay,
  fmtDecimal,
  fmtInt,
  gertrudisInsights,
  localesData,
  locationRows,
  monthlyGoogle,
  pct,
  postRows,
  starStats,
  str,
  toneOf,
  topicRows,
  truthy,
  whatIfFourStars,
  type MonthPoint,
  type Origen,
  type ToneCount,
  type TopicRow,
  isCriticalReview,
  TARGET_RATING,
  monthlyTrend,
} from "./resumenData";
import { TopicHighlights } from "./SandboxTemas";
import { PublicacionesConversacion } from "./PublicacionesConversacion";

export type SandboxTheme = "light" | "dark";

const PILL_LABEL: Record<string, string> = {
  all: "Todos los canales",
  gbp: CHAN_LABEL.gbp,
  meta: "Meta",
  meta_ig: CHAN_LABEL.meta_ig,
  meta_fb: CHAN_LABEL.meta_fb,
  youtube: CHAN_LABEL.youtube,
};

interface Props {
  clientSlug: string;
  overview: SocialOverviewPayload | null;
  loading: boolean;
  /** Comentarios del rango activo, sin respuestas propias. */
  rangeComments: DataRecord[];
  /** Historial completo disponible para la serie mensual. */
  allComments: DataRecord[];
  /** Todas las fichas de Google del cliente, con o sin reseñas en el período. */
  locales?: GoogleLocal[];
  /** Extremos del filtro de fechas (ISO); null = sin límite. Definen los meses del gráfico mensual. */
  rangeFrom?: string | null;
  rangeTo?: string | null;
  platform: SocialPlatformFilter;
  availablePlatforms: SocialPlatformFilter[];
  onPlatformChange: (p: SocialPlatformFilter) => void;
  periodLabel: string;
  /** Tema activo. Lo define el interruptor de la página del sandbox. */
  theme: SandboxTheme;
  /** Comentarios del período anterior de igual largo. null si no hay período anterior. */
  prevComments?: DataRecord[] | null;
  /** Fechas del período anterior, para explicarlas en pantalla. */
  prevLabel?: string;
  /** Navega a la bandeja de respuesta */
  onNavigateToBandeja?: () => void;
  /** Abre la Bandeja en un comentario de una publicación. */
  onOpenComment?: (feedbackId: string) => void;
  /** Origen elegido en el selector de Social; se suma a la línea de canal del PDF. */
  origen?: Origen;
  /** Abre la vista Locales. Si no viene, el enlace baja a la sección de Locales de esta página. */
  onNavigateToLocales?: (localId?: string) => void;
  /** Abre la vista Temas, con un tema elegido si viene. Si no viene, los enlaces bajan a la sección de Temas de esta página. */
  onNavigateToTemas?: (topic?: string) => void;
  /** Si debe renderizar su propia cabecera sticky o delegarla al contenedor */
  showHeader?: boolean;
  /** El contenedor lee de aquí la función que genera el informe PDF del Resumen. */
  exportRef?: React.MutableRefObject<(() => void) | null>;
}

type DeltaTone = "good" | "bad" | "flat";

function deltaView(delta: unknown, negativeGood: boolean, emptyText: string): { text: string; tone: DeltaTone } {
  const d = str(delta).trim();
  if (!d || d === "n/d") return { text: emptyText, tone: "flat" };
  const plus = d.startsWith("+");
  const minus = d.startsWith("-");
  const zero = /^[+-]?0([.,]0+)?\s*(pp|%)?$/.test(d);
  const body = d.replace(/^[+-]/, "").replace(".", ",");
  if (zero || (!plus && !minus)) return { text: `${d.replace(".", ",")} vs período anterior`, tone: "flat" };
  const good = negativeGood ? minus : plus;
  return { text: `${plus ? "▲" : "▼"} ${body} vs período anterior`, tone: good ? "good" : "bad" };
}

/** Etiqueta de estado de un local, con las mismas reglas en escritorio y teléfono. */
function localTag(avg: number | null, ls: ReturnType<typeof localesData>[number] | undefined): { label: string; cls: string } {
  if (!ls) {
    return avg !== null && avg >= 4.3
      ? { label: "Sostiene la nota", cls: "good" }
      : avg !== null && avg >= 4.0
      ? { label: "Estable", cls: "stable" }
      : { label: "Requiere atención", cls: "bad" };
  }
  return {
    label: ls.label,
    cls:
      ls.status === "attention" || ls.status === "review"
        ? "bad"
        : ls.status === "improving" || ls.status === "holds"
        ? "good"
        : "stable",
  };
}

/** Lleva la vista a una sección del Resumen, sin cambiar la dirección de la página. En teléfono las secciones son pestañas: se avisa con un evento. */
function scrollToSection(id: string) {
  const el = document.querySelector<HTMLElement>(`.sbx #${id}`);
  if (!el) {
    window.dispatchEvent(new CustomEvent("sbx-goto", { detail: id }));
    return;
  }
  const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
}

/** Neto del tema: % positivos menos % negativos. Azul si suma, naranja si resta. */
function netView(net: number | null, classified: number): { text: string; tone: DeltaTone } {
  if (net === null) return { text: "Sin clasificar", tone: "flat" };
  // Con menos de 5 comentarios clasificados la cifra no es concluyente: se muestra en gris
  if (classified < 5) return { text: net > 0 ? `+${net}` : `${net}`, tone: "flat" };
  if (net > 0) return { text: `+${net}`, tone: "good" };
  if (net < 0) return { text: `${net}`, tone: "bad" };
  return { text: "0", tone: "flat" };
}

/** Cambio en quejas del tema contra el período anterior. Menos quejas es bueno. */
function trendView(r: TopicRow): { text: string; tone: DeltaTone } {
  if (r.negPrev === null) return { text: "Sin período anterior", tone: "flat" };
  if (r.negNow === 0 && r.negPrev === 0) return { text: "Sin quejas", tone: "flat" };
  const diff = r.negNow - r.negPrev;
  if (diff === 0) return { text: `Sin cambio (${r.negNow})`, tone: "flat" };
  const abs = Math.abs(diff);
  // Con pocas quejas un porcentaje engaña: se muestra la cantidad
  const size = r.negPrev >= 5 ? `${Math.round((abs / r.negPrev) * 100)}%` : `${abs} ${abs === 1 ? "queja" : "quejas"}`;
  return { text: `${diff > 0 ? "▲" : "▼"} ${size} (de ${r.negPrev} a ${r.negNow})`, tone: diff > 0 ? "bad" : "good" };
}

function ToneBar({ tone, height = 12 }: { tone: ToneCount; height?: number }) {
  const classified = tone.pos + tone.neu + tone.neg;
  const w = (n: number) => `${classified > 0 ? ((n / classified) * 100).toFixed(1) : 0}%`;
  return (
    <div
      className="sbx-tonebar"
      style={{ height }}
      role="img"
      aria-label={`${tone.pos} positivos, ${tone.neu} neutros, ${tone.neg} negativos`}
    >
      <i className="pos" style={{ width: w(tone.pos) }} />
      <i className="neu" style={{ width: w(tone.neu) }} />
      <i className="neg" style={{ width: w(tone.neg) }} />
    </div>
  );
}

function ToneLegend({ tone }: { tone: ToneCount }) {
  const classified = tone.pos + tone.neu + tone.neg;
  return (
    <div className="sbx-legend">
      <span>{pct(tone.pos, classified)}% positivos</span>
      <span>{pct(tone.neu, classified)}% neutros</span>
      <span>{pct(tone.neg, classified)}% negativos</span>
      {tone.none > 0 && <span>{fmtInt(tone.none)} sin clasificar</span>}
    </div>
  );
}

function Section({
  id,
  tag,
  title,
  subtitle,
  children,
}: {
  id?: string;
  tag: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="sbx-section">
      <div className="sbx-section-head">
        <span className="sbx-tag">{tag}</span>
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

/** Leyenda del gráfico mensual: nota, mes en curso, zona objetivo y tendencia con su pendiente por mes. */
export function MonthlyLegend({ pts }: { pts: MonthPoint[] }) {
  const trend = useMemo(() => monthlyTrend(pts), [pts]);
  const per = trend ? `${trend.slope >= 0 ? "+" : "-"}${fmtDecimal(Math.abs(trend.slope), 2)}` : null;
  return (
    <div className="sbx-chart-legend">
      <span>
        <i />
        Nota
      </span>
      <span>
        <i className="dash" />
        Mes en curso
      </span>
      <span>
        <i className="band" />
        Objetivo {fmtDecimal(TARGET_RATING)}+
      </span>
      {per && (
        <span>
          <i className="trend" />
          Tendencia {per} por mes
        </span>
      )}
    </div>
  );
}

export function MonthlyChart({ pts }: { pts: MonthPoint[] }) {
  const [hover, setHover] = React.useState<number | null>(null);
  const [pinned, setPinned] = React.useState<number | null>(null);
  const active = pinned !== null ? pinned : hover;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [boxW, setBoxW] = React.useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBoxW(el.clientWidth));
    setBoxW(el.clientWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const trend = useMemo(() => monthlyTrend(pts), [pts]);
  const rated = pts.filter((p) => p.avg !== null);
  const minV = rated.length ? Math.min(...rated.map((p) => p.avg as number)) : 3;
  // Eje de nota dinámico: baja hasta el entero bajo la nota más baja y siempre deja ver la meta.
  const lo = Math.max(1, Math.min(Math.floor(TARGET_RATING - 0.5), Math.floor(minV - 0.05)));
  const tickStep = 5 - lo > 2 ? 1 : 0.5;
  const plotL = 34;
  const padR = 16;
  const plotTop = 20;
  const plotH = 170;
  const plotBot = plotTop + plotH;
  const barMax = 56;
  const baseY = plotBot + 18 + barMax;
  const H = baseY + 24;
  const yOf = (v: number) => plotTop + ((5 - v) / (5 - lo)) * plotH;
  const n = pts.length;
  const avail = Math.max(boxW || 640, 280);
  const needed = plotL + n * 24 + padR;
  const W = Math.max(avail, needed);
  const wide = needed > avail;
  const step = (W - plotL - padR) / Math.max(1, n);
  const xOf = (i: number) => plotL + step * (i + 0.5);
  const showMonthLabel = (p: MonthPoint) => step >= 34 || Number(p.key.slice(5)) % 3 === 1;
  const monthLabel = (p: MonthPoint) => (n > 12 && p.key.endsWith("-01") ? `${p.label} ’${String(p.year).slice(2)}` : p.label);
  const maxCount = Math.max(1, ...pts.map((p) => p.count));
  const showCounts = step >= 34;
  const ticks: number[] = [];
  for (let v = 5; v >= lo - 1e-9; v -= tickStep) ticks.push(v);

  // En teléfono, con muchos meses el gráfico se desplaza: arranca en los más recientes.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && wide) el.scrollLeft = el.scrollWidth;
  }, [pts.length, wide]);

  // Tramos continuos de meses cerrados con reseñas
  const segments: string[] = [];
  let run: string[] = [];
  pts.forEach((p, i) => {
    if (p.avg !== null && !p.current) {
      run.push(`${xOf(i)},${yOf(p.avg)}`);
    } else {
      if (run.length > 1) segments.push(run.join(" "));
      run = [];
    }
  });
  if (run.length > 1) segments.push(run.join(" "));

  const last = pts.length - 1;
  const cur = pts[last];
  const prev = pts[last - 1];
  const labelFrom = Math.max(0, rated.length ? pts.lastIndexOf(rated[rated.length - 1]) - 1 : 0);
  const activePt = active !== null ? pts[active] : undefined;

  const hitLabel = (p: MonthPoint) =>
    `${p.monthName} ${p.year}${p.current ? " (en curso)" : ""}: ${
      p.avg !== null ? `nota ${fmtDecimal(p.avg)}, ${p.count} reseñas` : "sin reseñas"
    }`;

  const onHitKey = (e: React.KeyboardEvent<SVGRectElement>, i: number) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setPinned((c) => (c === i ? null : i));
    } else if (e.key === "Escape") {
      setPinned(null);
    }
  };

  return (
    <div className="sbx-chart-scroll" ref={scrollRef} style={wide ? { overflowX: "auto" } : undefined}>
    {wide && <span className="sbx-m-hint">Desliza para ver los {n} meses</span>}
    <div className="sbx-chart-wrap" style={wide ? { minWidth: W } : undefined} onPointerLeave={() => setHover(null)}>
      <svg
        className="sbx-svg"
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="group"
        aria-label={`Nota mensual en Google de ${n} meses y reseñas recibidas por mes`}
      >
        <rect className="goal" x={plotL} y={yOf(5)} width={W - plotL - padR + 4} height={yOf(TARGET_RATING) - yOf(5)} />
        {ticks.map((v) => (
          <g key={v}>
            <text className={v === TARGET_RATING ? "goal-label" : undefined} x="0" y={yOf(v) + 4}>{fmtDecimal(v)}</text>
            <line className={v === lo ? "base" : "grid"} x1={plotL} x2={W - padR + 4} y1={yOf(v)} y2={yOf(v)} />
          </g>
        ))}
        {!ticks.includes(TARGET_RATING) && (
          <text className="goal-label" x="0" y={yOf(TARGET_RATING) + 4}>{fmtDecimal(TARGET_RATING)}</text>
        )}
        <line className="goal-line" x1={plotL} x2={W - padR + 4} y1={yOf(TARGET_RATING)} y2={yOf(TARGET_RATING)} />
        {pts.map((p, i) => (
          <rect
            key={`hit-${p.key}`}
            className={active === i ? "hit on" : "hit"}
            x={xOf(i) - step / 2}
            y="8"
            width={step}
            height={H - 14}
            tabIndex={0}
            role="img"
            aria-label={hitLabel(p)}
            onPointerEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
            onClick={() => setPinned((c) => (c === i ? null : i))}
            onKeyDown={(e) => onHitKey(e, i)}
          />
        ))}
        {active !== null && <line className="cross" x1={xOf(active)} x2={xOf(active)} y1="14" y2={baseY} />}
        {pts.map((p, i) => {
          const h = p.count > 0 ? Math.max(3, (p.count / maxCount) * barMax) : 0;
          const bw = Math.min(step * 0.5, 30);
          return (
            <g key={p.key}>
              <rect className={p.current ? "bar now" : "bar"} x={xOf(i) - bw / 2} y={baseY - h} width={bw} height={h} />
              {showCounts && p.count > 0 && (
                <text className="count" x={xOf(i)} y={baseY - h - 4} textAnchor="middle">
                  {p.count}
                </text>
              )}
            </g>
          );
        })}
        {trend && (
          <line
            className="trend"
            x1={xOf(trend.from)}
            y1={yOf(Math.min(5, Math.max(lo, trend.at(trend.from))))}
            x2={xOf(last)}
            y2={yOf(Math.min(5, Math.max(lo, trend.at(last))))}
          />
        )}
        {segments.map((s) => (
          <polyline key={s} className="line" points={s} />
        ))}
        {cur && prev && cur.current && cur.avg !== null && prev.avg !== null && (
          <line
            className="line dash"
            x1={xOf(last - 1)}
            y1={yOf(prev.avg)}
            x2={xOf(last)}
            y2={yOf(cur.avg)}
          />
        )}
        {pts.map((p, i) =>
          p.avg === null ? null : (
            <circle
              key={p.key}
              className={`${p.current ? "dot open" : "dot"}${active === i ? " active" : ""}`}
              cx={xOf(i)}
              cy={yOf(p.avg)}
              r={active === i ? 6 : 4}
            />
          ),
        )}
        {pts.map((p, i) =>
          p.avg !== null && i >= labelFrom ? (
            <text key={`v-${p.key}`} className="strong" x={xOf(i) - 8} y={yOf(p.avg) - 10}>
              {fmtDecimal(p.avg)}
            </text>
          ) : null,
        )}
        {pts.map((p, i) =>
          showMonthLabel(p) ? (
            <text key={`m-${p.key}`} className={active === i ? "strong" : undefined} x={xOf(i)} y={baseY + 16} textAnchor="middle">
              {monthLabel(p)}
              {p.current ? "*" : ""}
            </text>
          ) : null,
        )}
      </svg>

      {activePt && active !== null && (
        <div
          className={`sbx-tip${active < 2 ? " edge-l" : active > pts.length - 3 ? " edge-r" : ""}${
            activePt.avg !== null && yOf(activePt.avg) < 125 ? " below" : ""
          }`}
          style={{
            left: `${(xOf(active) / W) * 100}%`,
            top: `${((activePt.avg !== null ? yOf(activePt.avg) : 120) / H) * 100}%`,
          }}
        >
          <h4>
            {activePt.monthName} {activePt.year}
            {activePt.current ? " · en curso" : ""}
          </h4>
          {activePt.avg !== null ? (
            <dl>
              <dt>Nota promedio</dt>
              <dd>{fmtDecimal(activePt.avg)} ★</dd>
              <dt>Reseñas recibidas</dt>
              <dd>{fmtInt(activePt.count)}</dd>
              <dt>De 4 a 5 ★</dt>
              <dd>{fmtInt(activePt.high)}</dd>
              <dt>De 1 a 2 ★</dt>
              <dd>{fmtInt(activePt.low)}</dd>
            </dl>
          ) : (
            <p>Sin reseñas este mes</p>
          )}
          {pinned === active && <p className="sbx-tip-hint">Clic para soltar</p>}
        </div>
      )}
    </div>
    </div>
  );
}

/** Reparto de estrellas en una franja apilada, con el conteo y porcentaje de cada nota debajo. */
export function StarSplit({
  stars,
  whatIf,
  caption,
}: {
  stars: ReturnType<typeof starStats>;
  whatIf?: ReturnType<typeof whatIfFourStars>;
  caption: string;
}) {
  return (
    <div className="sbx-starsplit">
      <div className="sbx-starsplit-head">
        <strong>Cómo se reparten las estrellas</strong>
        <span>{caption}</span>
      </div>
      <div
        className="sbx-starsplit-bar"
        role="img"
        aria-label={[5, 4, 3, 2, 1].map((s) => `${s} estrellas: ${stars.counts[s] ?? 0}`).join(", ")}
      >
        {[5, 4, 3, 2, 1].map((s) =>
          (stars.counts[s] ?? 0) > 0 ? (
            <i key={s} className={`s${s}`} style={{ flexGrow: stars.counts[s] }} />
          ) : null,
        )}
      </div>
      <div className="sbx-starsplit-legend">
        {[5, 4, 3, 2, 1].map((s) => (
          <div key={s} className={`s${s}`}>
            <b>{s} ★</b>
            <strong>{fmtInt(stars.counts[s] ?? 0)}</strong>
            <em>{pct(stars.counts[s] ?? 0, stars.total)}%</em>
          </div>
        ))}
      </div>
      {whatIf && (
        <p className="sbx-note">
          Si {whatIf.moved} {whatIf.moved === 1 ? "reseña de 1 estrella hubiera sido de 4" : "reseñas de 1 estrella hubieran sido de 4"}, la nota sería{" "}
          <strong>{fmtDecimal(whatIf.avg)}</strong>.
        </p>
      )}
    </div>
  );
}

export function SandboxResumen({
  clientSlug,
  overview,
  loading,
  rangeComments,
  allComments,
  locales = [],
  rangeFrom,
  rangeTo,
  platform,
  availablePlatforms,
  onPlatformChange,
  periodLabel,
  theme,
  prevComments = null,
  prevLabel = "",
  onNavigateToBandeja,
  onOpenComment,
  origen = "all",
  onNavigateToLocales,
  onNavigateToTemas,
  showHeader = true,
  exportRef,
}: Props) {
  const brand = getClientBrandMeta(clientSlug);
  const isMobile = useIsMobile();

  const comments = useMemo(
    () => {
      if (platform === "all") return rangeComments.filter((c) => channelOf(c) !== "youtube");
      if (platform === "meta") return rangeComments.filter((c) => channelOf(c) === "meta_ig" || channelOf(c) === "meta_fb");
      return rangeComments.filter((c) => channelOf(c) === platform);
    },
    [rangeComments, platform],
  );

  const channels = useMemo(() => channelStats(comments), [comments]);
  const stars = useMemo(() => starStats(comments), [comments]);
  const months = useMemo(() => monthlyGoogle(allComments, rangeFrom, rangeTo), [allComments, rangeFrom, rangeTo]);
  const whatIf = useMemo(() => whatIfFourStars(stars), [stars]);
  const googleLocales = useMemo(() => (platform === "all" || platform === "gbp" ? locales : []), [locales, platform]);
  const locations = useMemo(() => locationRows(comments, googleLocales), [comments, googleLocales]);
  const posts = useMemo(() => postRows(comments), [comments]);
  const prevScoped = useMemo(
    () => {
      if (!prevComments) return null;
      if (platform === "all") return prevComments;
      if (platform === "meta") return prevComments.filter((c) => channelOf(c) === "meta_ig" || channelOf(c) === "meta_fb");
      return prevComments.filter((c) => channelOf(c) === platform);
    },
    [prevComments, platform],
  );
  const topics = useMemo(() => topicRows(comments, prevScoped), [comments, prevScoped]);
  // Estado de cada local con las mismas reglas de la vista Locales, para que ambas pantallas digan lo mismo.
  const localStatus = useMemo(() => {
    const m = new Map<string, ReturnType<typeof localesData>[number]>();
    for (const r of localesData(allComments, comments, prevScoped, googleLocales)) m.set(r.name, r);
    return m;
  }, [allComments, comments, prevScoped, googleLocales]);

  const showGoogle = stars.total > 0 && (platform === "all" || platform === "gbp");
  const showLocales = locations.length > 0 && (showGoogle || googleLocales.length > 0);
  const hasMonthly = months.some((m) => m.avg !== null);
  const exportMonths = months.slice(-24);
  const pills: SocialPlatformFilter[] = availablePlatforms.length > 1 ? ["all", ...availablePlatforms] : [];
  const windowDays = overview?.window_days ?? 28;

  // Indicadores principales
  const kpis = overview?.kpis;
  const prevUniverse = overview?.universo_previo ?? 0;
  const currUniverse = overview?.universo_actual ?? 0;
  const volumeDelta = deltaView(
    prevUniverse > 0
      ? `${currUniverse >= prevUniverse ? "+" : "-"}${Math.abs(Math.round(((currUniverse - prevUniverse) / prevUniverse) * 100))}%`
      : "n/d",
    false,
    "Sin período anterior para comparar",
  );

  const nota = kpis?.nota_media;
  const notaValue = nota && typeof nota.value === "number" && nota.value > 0 ? nota.value : null;
  const neg = kpis?.pct_negativos;
  const negValue = neg && neg.value !== null && neg.value !== undefined ? Math.round(neg.value) : null;
  const respTime = kpis?.tiempo_respuesta_negativos?.display;
  const respTimeValid =
    Boolean(respTime) &&
    !String(respTime).toLowerCase().includes("sin respuestas") &&
    String(respTime).toLowerCase() !== "n/d" &&
    String(respTime).toLowerCase() !== "sin datos";
  const urgent = comments.filter(isCriticalReview).length;

  const sparklines = useMemo(() => computeDailySparklines(comments), [comments]);
  const insights = useMemo(
    () => (comments.length > 0 ? gertrudisInsights({ comments, topics, urgent }) : []),
    [comments, topics, urgent],
  );

  const actionPlan = useMemo(() => {
    const items: Array<{
      area: string;
      due: string;
      dueUrgent?: boolean;
      title: string;
      why: string;
      evidence: string;
      onAction: () => void;
    }> = [];

    if (urgent > 0) {
      items.push({
        area: "Comunicaciones",
        due: "Hoy",
        dueUrgent: true,
        title: `Responder los ${urgent} casos críticos pendientes`,
        why: "Comentarios que requieren atención inmediata para contener insatisfacción.",
        evidence: "Abrir bandeja",
        onAction: () => (onNavigateToBandeja ? onNavigateToBandeja() : scrollToSection("canales")),
      });
    }

    const adQueries = comments.filter(
      (c) =>
        str(c.origin).toLowerCase() === "paid" &&
        !truthy(c.has_reply) &&
        (str(c.acto_de_habla).toLowerCase() === "pregunta" || str(c.acto_de_habla).toLowerCase() === "solicitud_contacto"),
    );
    if (adQueries.length > 0) {
      items.push({
        area: "Pauta digital",
        due: "Hoy",
        dueUrgent: true,
        title: `Responder las ${adQueries.length} consultas en anuncios`,
        why: "Consultas de precio o despacho en pauta activa. Son ventas potenciales que se enfrían.",
        evidence: "Ver publicaciones",
        onAction: () => scrollToSection("publicaciones"),
      });
    }

    const worse = topics.rows
      .filter((t) => t.negPrev !== null && t.negNow >= 3 && t.negNow - (t.negPrev ?? 0) >= 2)
      .sort((a, b) => b.negNow - (b.negPrev ?? 0) - (a.negNow - (a.negPrev ?? 0)))[0];
    if (worse) {
      items.push({
        area: "Operaciones",
        due: "Esta semana",
        dueUrgent: false,
        title: `Revisar causas de quejas en «${worse.name}»`,
        why: `Las quejas subieron de ${worse.negPrev ?? 0} a ${worse.negNow} frente al período anterior.`,
        evidence: `Ver ${worse.total} comentarios`,
        onAction: () => (onNavigateToTemas ? onNavigateToTemas(worse.key) : scrollToSection("temas")),
      });
    }

    return items;
  }, [urgent, comments, topics.rows, onNavigateToBandeja, onNavigateToTemas]);

  const breakdown = channels.map((c) => `${CHAN_SHORT[c.chan]} ${fmtInt(c.total)}`).join(" · ");

  const exportPdf = async () => {
    const { buildResumenPdf } = await import("./resumenPdf");
    const tonePct = (t: ToneCount, k: "pos" | "neu" | "neg") => `${pct(t[k], t.pos + t.neu + t.neg)}%`;
    const sections: ReportSection[] = [];

    if (overview && currUniverse > 0) {
      const items = [];
      if (notaValue !== null && nota) {
        items.push({ label: "Nota promedio en Google", value: `${fmtDecimal(notaValue)} de 5`, sub: deltaView(nota.delta, false, "Sin variación").text });
      }
      items.push({ label: "Comentarios y reseñas", value: fmtInt(currUniverse), sub: volumeDelta.text });
      if (negValue !== null && neg) {
        items.push({ label: "Menciones negativas", value: `${negValue}%`, sub: deltaView(neg.delta, true, "Sin variación").text });
      }
      items.push({
        label: "Críticos sin responder",
        value: String(urgent),
        sub: respTimeValid ? `Tiempo medio ${respTime} · meta 24 h` : "Meta de respuesta 24 h",
      });
      sections.push({ tag: "Indicadores", title: "Indicadores principales", blocks: [{ kind: "kpis", items }] });
    }

    if (insights.length > 0) {
      sections.push({
        tag: "Resumen",
        title: "Alertas y oportunidades",
        blocks: [{ kind: "cards", items: insights.map((i) => ({ label: i.label, text: i.segs.map((s) => s.text).join("") })) }],
      });
    }

    if (channels.length > 0) {
      sections.push({
        tag: "Canales",
        title: "Cómo se comporta cada canal",
        blocks: [
          {
            kind: "table",
            head: ["Canal", "Tipo", "Total", "Nota", "Positivos", "Neutros", "Negativos", "Sin responder", "En anuncios"],
            rows: channels.map((c) => [
              CHAN_LABEL[c.chan],
              CHAN_KIND[c.chan],
              fmtInt(c.total),
              c.avg !== null ? fmtDecimal(c.avg) : "-",
              tonePct(c.tone, "pos"),
              tonePct(c.tone, "neu"),
              tonePct(c.tone, "neg"),
              fmtInt(c.negPending),
              c.chan === "gbp" || c.chan === "youtube" ? "No aplica" : fmtInt(c.paid),
            ]),
            numeric: [2, 3, 4, 5, 6, 7, 8],
          },
        ],
      });
    }

    if (topics.rows.length > 0) {
      sections.push({
        tag: "Temas",
        title: "De qué habla la gente, y en qué tono",
        blocks: [
          {
            kind: "table",
            head: ["Tema", "Menciones", "Positivos", "Neutros", "Negativos", "Neto", "Cambio en quejas"],
            rows: topics.rows.map((t) => [
              t.name,
              fmtInt(t.total),
              tonePct(t.tone, "pos"),
              tonePct(t.tone, "neu"),
              tonePct(t.tone, "neg"),
              netView(t.net, t.tone.pos + t.tone.neu + t.tone.neg).text,
              trendView(t).text,
            ]),
            numeric: [1, 2, 3, 4, 5],
            note: `${fmtInt(topics.assigned)} de ${fmtInt(topics.total)} comentarios tienen un tema asignado. El neto de los temas con menos de 5 comentarios clasificados no es concluyente.`,
          },
        ],
      });
    }

    if (posts.length > 0) {
      sections.push({
        tag: "Instagram y Facebook",
        title: "Publicaciones y anuncios con más conversación",
        blocks: [
          {
            kind: "table",
            head: ["Publicación o anuncio", "Canal", "Origen", "Comentarios", "Negativos", "Lo que más se repite", "Sin responder"],
            rows: posts.map((p) => [
              p.name,
              CHAN_SHORT[p.chan],
              p.paid ? "Anuncio" : "Orgánico",
              fmtInt(p.total),
              tonePct(p.tone, "neg"),
              p.repeated,
              fmtInt(p.pending),
            ]),
            numeric: [3, 4, 6],
          },
        ],
      });
    }

    if (showGoogle) {
      const blocks: ReportSection["blocks"] = [
        {
          kind: "bars",
          items: [5, 4, 3, 2, 1].map((s) => ({
            label: `${s} estrellas`,
            value: stars.counts[s] ?? 0,
            text: `${fmtInt(stars.counts[s] ?? 0)} · ${pct(stars.counts[s] ?? 0, stars.total)}%`,
          })),
        },
      ];
      if (whatIf) {
        blocks.push({
          kind: "text",
          text: `Si ${whatIf.moved} ${whatIf.moved === 1 ? "reseña de 1 estrella hubiera sido de 4" : "reseñas de 1 estrella hubieran sido de 4"}, la nota sería ${fmtDecimal(whatIf.avg)}.`,
        });
      }
      if (hasMonthly) {
        blocks.push({
          kind: "columns",
          max: 5,
          items: exportMonths.map((m) => ({ label: m.label, value: m.avg, text: m.avg !== null ? fmtDecimal(m.avg) : "" })),
        });
      }
      sections.push({
        tag: "Google",
        title: "Cómo se reparten las estrellas",
        subtitle: `${fmtInt(stars.total)} reseñas en Google del período. El gráfico de columnas muestra la nota mensual de los últimos ${exportMonths.length} meses.`,
        blocks,
      });
    }

    if (showLocales) {
      sections.push({
        tag: "Locales",
        title: "Dónde se gana y dónde se pierde confianza",
        blocks: [
          {
            kind: "table",
            head: ["Local", "Nota", "Reseñas", "Negativas", "Estado", "Queja principal"],
            rows: locations.map((l) => [
              l.name,
              l.avg !== null ? fmtDecimal(l.avg) : "-",
              fmtInt(l.reviews),
              `${l.lowPct}%`,
              localStatus.get(l.name)?.label ??
                (l.avg !== null && l.avg >= 4.3 ? "Sostiene la nota" : l.avg !== null && l.avg >= 4.0 ? "Estable" : "Requiere atención"),
              l.complaint ?? "-",
            ]),
            numeric: [1, 2, 3],
          },
        ],
      });
    }

    if (actionPlan.length > 0) {
      sections.push({
        tag: "Plan de acción",
        title: "Qué hacer esta semana",
        blocks: [{ kind: "table", head: ["Área", "Plazo", "Acción", "Por qué"], rows: actionPlan.map((a) => [a.area, a.due, a.title, a.why]) }],
      });
    }

    const criticos = comments
      .filter(isCriticalReview)
      .sort((a, b) => str(a.date).localeCompare(str(b.date)));
    if (criticos.length > 0) {
      const texto = (c: (typeof criticos)[number]) => {
        const t = cleanReviewText(c.message) || "Sin texto";
        return t.length > 200 ? `${t.slice(0, 197).trimEnd()}...` : t;
      };
      sections.push({
        tag: "Bandeja",
        title: "Casos críticos sin responder",
        subtitle: "Los más antiguos primero.",
        blocks: [
          {
            kind: "table",
            head: ["Fecha", "Canal", "Local", "Estrellas", "Comentario"],
            rows: criticos.slice(0, 15).map((c) => {
              const chan = channelOf(c);
              const stars = Number(c.star_rating);
              return [fmtDay(str(c.date)), chan ? CHAN_SHORT[chan] : "-", str(c.post_title).trim() || "-", stars > 0 ? String(stars) : "-", texto(c)];
            }),
            numeric: [3],
            note: criticos.length > 15 ? `Se muestran 15 de ${fmtInt(criticos.length)} casos críticos sin responder.` : undefined,
          },
        ],
      });
    }

    try {
      const mensajes = await fetchSocialMensajes(clientSlug);
      if (mensajes.plataformas.length > 0) {
        const nombres: Record<string, string> = { messenger: "Facebook (Messenger)", instagram: "Instagram" };
        const p100 = (v: number | null) => (typeof v === "number" ? `${Math.round(v * 100)}%` : "-");
        const n = (v: number | null) => (typeof v === "number" ? fmtInt(v) : "-");
        sections.push({
          tag: "Mensajes directos",
          title: "Conversaciones por plataforma",
          blocks: [
            {
              kind: "table",
              head: ["Plataforma", "Conversaciones", "Sin respuesta", "Nunca respondidas", "Informativas", "Posibles leads", "Desde", "Hasta"],
              rows: mensajes.plataformas.map((m) => [
                nombres[m.platform] ?? m.platform,
                n(m.conversaciones),
                `${n(m.sin_respuesta)} (${p100(m.pct_sin_respuesta)})`,
                `${n(m.nunca_respondidas)} (${p100(m.pct_nunca_respondidas)})`,
                `${n(m.informativas)} (${p100(m.pct_informativas)})`,
                `${n(m.posibles_leads)} (${p100(m.pct_posibles_leads)})`,
                m.primera_fecha ?? "-",
                m.ultima_fecha ?? "-",
              ]),
              numeric: [1],
              note: "Los mensajes directos no dependen del rango de fechas ni del canal elegidos arriba.",
            },
          ],
        });
      }
    } catch {
      /* Sin acceso a mensajes (usuario de cliente) o sin conexión: el informe sigue sin esta sección. */
    }

    const now = new Date();
    buildResumenPdf({
      clientName: brand.displayName,
      period: periodLabel,
      channel: `${PILL_LABEL[platform] ?? platform}${origen === "all" ? "" : ` · Solo ${origen === "paid" ? "anuncios" : "orgánicos"}`}`,
      generatedAt: now.toLocaleString("es-CL", { timeZone: "America/Santiago", dateStyle: "long", timeStyle: "short" }),
      sections,
      footer: "Wolf Digital · Social Wolf",
      fileName: `social-wolf-${clientSlug}-${now.toLocaleDateString("sv-SE", { timeZone: "America/Santiago" })}.pdf`,
    });
  };

  useEffect(() => {
    if (!exportRef) return;
    exportRef.current = () => void exportPdf();
    return () => {
      exportRef.current = null;
    };
  });

  const renderKpis = () => {
    if (loading && !overview) return <div className="sbx-empty">Cargando indicadores...</div>;
    if (!overview) return <div className="sbx-empty">No hay indicadores disponibles para este período.</div>;
    if (currUniverse === 0) {
      return <div className="sbx-empty">No hay actividad registrada en los últimos {windowDays} días.</div>;
    }
    const negCount = comments.filter((c) => toneOf(c) === "neg").length;
    return (
      <div className="sbx-grid-kpi">
        {notaValue !== null && nota && (
          <div className="sbx-card sbx-kpi">
            <span className="sbx-kpi-label">Nota promedio en Google</span>
            <div className="sbx-kpi-value">
              <strong>{fmtDecimal(notaValue)}</strong>
              <span>de 5</span>
            </div>
            <span className={`sbx-kpi-delta is-${deltaView(nota.delta, false, "Sin variación").tone}`}>
              {deltaView(nota.delta, false, "Sin variación").text}
            </span>
            {sparklines.googlePoints && (
              <svg viewBox="0 0 200 36" className="sbx-sparkline blue" aria-hidden="true">
                <polyline points={sparklines.googlePoints} />
              </svg>
            )}
            {stars.total > 0 && <span className="sbx-kpi-sub">{fmtInt(stars.total)} reseñas · meta 4,3</span>}
          </div>
        )}

        <div className="sbx-card sbx-kpi">
          <span className="sbx-kpi-label">Comentarios y reseñas</span>
          <div className="sbx-kpi-value">
            <strong>{fmtInt(currUniverse)}</strong>
          </div>
          <span className={`sbx-kpi-delta is-${volumeDelta.tone}`}>{volumeDelta.text}</span>
          {sparklines.volumePoints && (
            <svg viewBox="0 0 200 36" className="sbx-sparkline gray" aria-hidden="true">
              <polyline points={sparklines.volumePoints} />
            </svg>
          )}
          {breakdown && <span className="sbx-kpi-sub">{breakdown}</span>}
        </div>

        {negValue !== null && neg && (
          <div className="sbx-card sbx-kpi">
            <span className="sbx-kpi-label">Menciones negativas</span>
            <div className="sbx-kpi-value">
              <strong>{negValue}%</strong>
              {currUniverse > 0 && <span>{fmtInt(negCount)} de {fmtInt(currUniverse)}</span>}
            </div>
            <span className={`sbx-kpi-delta is-${deltaView(neg.delta, true, "Sin variación").tone}`}>
              {deltaView(neg.delta, true, "Sin variación").text}
            </span>
            {sparklines.negPoints && (
              <svg viewBox="0 0 200 36" className="sbx-sparkline red" aria-hidden="true">
                <polyline points={sparklines.negPoints} />
              </svg>
            )}
          </div>
        )}

        <div className={`sbx-card sbx-kpi${urgent > 0 ? " is-critical" : ""}`}>
          <span className="sbx-kpi-label">Críticos sin responder</span>
          <div className="sbx-kpi-value">
            <strong>{urgent}</strong>
            {urgent > 0 && <span>requieren atención</span>}
          </div>
          <span className="sbx-kpi-sub">
            {respTimeValid ? `Tiempo medio de respuesta ${respTime} · meta 24 h` : "Meta de respuesta 24 h"}
          </span>
          {urgent > 0 && (
            <button
              type="button"
              className="sbx-kpi-cta"
              onClick={onNavigateToBandeja ? onNavigateToBandeja : () => scrollToSection("canales")}
            >
              Atender ahora
            </button>
          )}
        </div>
      </div>
    );
  };

  if (isMobile) {
    const negCount = comments.filter((c) => toneOf(c) === "neg").length;
    const kpiMessage =
      loading && !overview
        ? "Cargando indicadores..."
        : !overview
        ? "No hay indicadores disponibles para este período."
        : currUniverse === 0
        ? `No hay actividad registrada en los últimos ${windowDays} días.`
        : null;
    const kpiCards: MobileKpi[] = [];
    if (notaValue !== null && nota) {
      kpiCards.push({
        id: "nota",
        label: "Nota promedio en Google",
        value: fmtDecimal(notaValue),
        unit: "de 5",
        delta: deltaView(nota.delta, false, "Sin variación"),
        spark: sparklines.googlePoints ? { points: sparklines.googlePoints, tone: "blue" } : undefined,
        sub: stars.total > 0 ? `${fmtInt(stars.total)} reseñas · meta 4,3` : undefined,
      });
    }
    kpiCards.push({
      id: "volumen",
      label: "Comentarios y reseñas",
      value: fmtInt(currUniverse),
      delta: volumeDelta,
      spark: sparklines.volumePoints ? { points: sparklines.volumePoints, tone: "gray" } : undefined,
      sub: breakdown || undefined,
    });
    if (negValue !== null && neg) {
      kpiCards.push({
        id: "neg",
        label: "Menciones negativas",
        value: `${negValue}%`,
        unit: currUniverse > 0 ? `${fmtInt(negCount)} de ${fmtInt(currUniverse)}` : undefined,
        delta: deltaView(neg.delta, true, "Sin variación"),
        spark: sparklines.negPoints ? { points: sparklines.negPoints, tone: "red" } : undefined,
      });
    }
    kpiCards.push({
      id: "criticos",
      label: "Críticos sin responder",
      value: String(urgent),
      unit: urgent > 0 ? "requieren atención" : undefined,
      sub: respTimeValid ? `Tiempo medio de respuesta ${respTime} · meta 24 h` : "Meta de respuesta 24 h",
      critical: urgent > 0,
      cta: urgent > 0 ? { label: "Atender ahora", onClick: onNavigateToBandeja ?? (() => scrollToSection("canales")) } : undefined,
    });

    return (
      <div className={`sbx${loading ? " is-loading" : ""}`} data-theme={theme}>
        {loading && (
          <div className="sbx-loading-badge" role="status" aria-live="polite">
            <span className="sbx-spinner" aria-hidden="true" />
            Cargando…
          </div>
        )}
        <ResumenMobile
          theme={theme}
          pills={[]}
          platform={platform}
          onPlatformChange={(id) => onPlatformChange(id as SocialPlatformFilter)}
          insights={loading && !overview ? [] : insights}
          onInsight={scrollToSection}
          kpiMessage={kpiMessage}
          kpis={kpiCards}
          channels={channels}
          topics={topics}
          onOpenTema={onNavigateToTemas}
          rating={
            showGoogle
              ? {
                  title: `Nota mensual en Google, ${months.length} meses`,
                  months,
                  stars,
                  starsCaption: `${fmtInt(stars.total)} reseñas del período`,
                  whatIf,
                }
              : null
          }
          locals={
            showLocales
              ? locations.map((l) => {
                  const ls = localStatus.get(l.name);
                  return { name: l.name, avg: l.avg, reviews: l.reviews, lowPct: l.lowPct, complaint: l.complaint, tag: localTag(l.avg, ls), id: ls?.id };
                })
              : []
          }
          onOpenLocal={onNavigateToLocales}
          posts={posts}
          onOpenComment={onOpenComment}
          actions={actionPlan}
        />
      </div>
    );
  }

  return (
    <div className={`sbx${loading ? " is-loading" : ""}`} data-theme={theme}>
      {loading && (
        <div className="sbx-loading-badge" role="status" aria-live="polite">
          <span className="sbx-spinner" aria-hidden="true" />
          Cargando…
        </div>
      )}
      {showHeader && (
        <header className="sbx-top-bar">
          <div className="sbx-top-bar-inner">
            <div className="sbx-brand">
              <span className="sbx-brand-title">Social Wolf</span>
            </div>

            <nav className="sbx-nav" aria-label="Secciones">
              <button type="button" className="sbx-nav-link active">
                Resumen
              </button>
              <button
                type="button"
                className="sbx-nav-link"
                onClick={onNavigateToBandeja ? onNavigateToBandeja : () => scrollToSection("canales")}
              >
                Bandeja {urgent > 0 && <span className="sbx-nav-badge">{urgent}</span>}
              </button>
              <button type="button" className="sbx-nav-link" onClick={() => scrollToSection("canales")}>
                Canales
              </button>
              <button type="button" className="sbx-nav-link" onClick={() => (onNavigateToTemas ? onNavigateToTemas() : scrollToSection("temas"))}>
                Temas
              </button>
              {showLocales && (
                <button
                  type="button"
                  className="sbx-nav-link"
                  onClick={() => (onNavigateToLocales ? onNavigateToLocales() : scrollToSection("locales"))}
                >
                  Locales
                </button>
              )}
              <button type="button" className="sbx-nav-link" onClick={() => scrollToSection("plan")}>
                Plan
              </button>
            </nav>

            <div className="sbx-top-right">
              <button type="button" className="sbx-btn-pdf" onClick={() => void exportPdf()}>
                Exportar PDF
              </button>
            </div>
          </div>
        </header>
      )}

      <section className="sbx-head">
        <div className="sbx-head-title">
          <h1>Resumen</h1>
        </div>
      </section>

      {/* Se espera a los indicadores para que el número de críticos no cambie al cargar */}
      {insights.length > 0 && !(loading && !overview) && (
        <section className="sbx-gert" aria-label="Alertas y oportunidades">
          <div className="sbx-gert-grid">
            {insights.map((i) => (
              <div key={i.id} className="sbx-gert-card">
                <span className={`sbx-gert-label ${i.tone}`}>{i.label}</span>
                <p>
                  {i.segs.map((s, idx) => (s.bold ? <strong key={idx}>{s.text}</strong> : <span key={idx}>{s.text}</span>))}
                </p>
                <button type="button" onClick={() => scrollToSection(i.target)}>
                  {i.cta}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section aria-label="Indicadores principales">{renderKpis()}</section>

      {channels.length > 0 && (
        <Section
          id="canales"
          tag="Canales"
          title="Cómo se comporta cada canal"
        >
          <div className="sbx-grid-cards">
            {channels.map((c) => (
              <div key={c.chan} className="sbx-card">
                <div className="sbx-card-top">
                  <strong>{CHAN_LABEL[c.chan]}</strong>
                  <span>{CHAN_KIND[c.chan]}</span>
                </div>
                <div className="sbx-big">
                  <strong>{fmtInt(c.total)}</strong>
                  {c.avg !== null && <span>Nota {fmtDecimal(c.avg)} de 5</span>}
                  {c.chan !== "gbp" && c.chan !== "youtube" && (
                    <span>
                      {fmtInt(c.total - c.paid)} en publicaciones · {fmtInt(c.paid)} en anuncios
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <ToneBar tone={c.tone} />
                  <ToneLegend tone={c.tone} />
                </div>
                <div className="sbx-stats">
                  <div>
                    Sin responder
                    <strong className={c.negPending > 0 ? "is-bad" : undefined}>{fmtInt(c.negPending)}</strong>
                  </div>
                  <div>
                    En anuncios
                    <strong>{c.chan === "gbp" || c.chan === "youtube" ? "No aplica" : fmtInt(c.paid)}</strong>
                  </div>
                </div>
                {c.topTopic && (
                  <p className="sbx-note">
                    Tema principal: <strong>{c.topTopic}</strong>
                  </p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {topics.rows.length > 0 && (
        <Section
          id="temas"
          tag="Temas"
          title="De qué habla la gente, y en qué tono"
        >
          <TopicHighlights rows={topics.rows} onSelect={onNavigateToTemas ? (key) => onNavigateToTemas(key) : undefined} />
          <p className="sbx-table-note" style={{ borderTop: "none", padding: 0 }}>
            {fmtInt(topics.assigned)} de {fmtInt(topics.total)} comentarios tienen un tema asignado.
          </p>
          {onNavigateToTemas && (
            <button type="button" className="sbx-link" onClick={() => onNavigateToTemas()}>
              Ver todos los temas y revisar sus menciones
            </button>
          )}
        </Section>
      )}

      {posts.length > 0 && (
        <Section
          id="publicaciones"
          tag="Instagram y Facebook"
          title="Publicaciones y anuncios con más conversación"
        >
          <PublicacionesConversacion posts={posts} onOpenComment={onOpenComment} />
        </Section>
      )}

      {showGoogle && (
        <section className="sbx-row" aria-label="Evolución de la nota en Google">
          {isMobile ? (
            <RatingCardMobile
              title={`Nota mensual en Google, ${months.length} meses`}
              months={months}
              stars={stars}
              starsCaption={`${fmtInt(stars.total)} reseñas del período`}
              whatIf={whatIf}
            />
          ) : (
            <div className="sbx-card sbx-card-wide">
              {hasMonthly && (
                <>
                  <div>
                    <h3>Nota mensual en Google, {months.length} meses</h3>
                    <p>Barras grises: reseñas recibidas cada mes. El mes con asterisco está en curso.</p>
                  </div>
                  <MonthlyLegend pts={months} />
                  <MonthlyChart pts={months} />
                </>
              )}
              <StarSplit stars={stars} whatIf={whatIf} caption={`${fmtInt(stars.total)} reseñas en Google del período`} />
            </div>
          )}
        </section>
      )}

      {showLocales && (
        <Section
          id="locales"
          tag="Locales"
          title="Dónde se gana y dónde se pierde confianza"
        >
          <div className="sbx-grid-cards">
            {locations.map((l) => {
              const ls = localStatus.get(l.name);
              const tag = localTag(l.avg, ls);
              const isBad = ls ? ls.status === "attention" : tag.cls === "bad";
              return (
                <div key={l.name} className="sbx-card" style={isBad ? { borderColor: "var(--sbx-crit)" } : undefined}>
                  <div className="sbx-card-top" style={{ alignItems: "center" }}>
                    <strong>{l.name}</strong>
                    <span className={`sbx-loc-tag ${tag.cls}`}>{tag.label}</span>
                  </div>
                  <div className="sbx-big">
                    <strong>{l.avg !== null ? fmtDecimal(l.avg) : "-"}</strong>
                    <span>de 5</span>
                  </div>
                  <div className="sbx-stats">
                    <div>
                      Reseñas
                      <strong>{fmtInt(l.reviews)}</strong>
                    </div>
                    <div>
                      Negativas
                      <strong className={l.lowPct >= 15 ? "is-bad" : undefined}>{l.reviews > 0 ? `${l.lowPct}%` : "-"}</strong>
                    </div>
                  </div>
                  {l.complaint && (
                    <p className="sbx-note">
                      Queja principal: <strong>{l.complaint}</strong>
                    </p>
                  )}
                  {onNavigateToLocales && ls && (
                    <button type="button" className="sbx-link" onClick={() => onNavigateToLocales(ls.id)}>
                      Ver el detalle de este local
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {onNavigateToLocales && (
            <button type="button" className="sbx-link" onClick={() => onNavigateToLocales()}>
              Ver el comparativo y el detalle de cada local
            </button>
          )}
        </Section>
      )}

      <Section id="plan" tag="Plan de acción" title="Qué hacer esta semana">
        {actionPlan.length > 0 ? (
          <div className="sbx-grid-cards">
            {actionPlan.map((a, idx) => (
              <div key={idx} className="sbx-action-card">
                <div className="sbx-action-head">
                  <span className="sbx-action-area">{a.area}</span>
                  <span className={`sbx-action-due${a.dueUrgent ? " urgent" : ""}`}>{a.due}</span>
                </div>
                <p className="sbx-action-title">{a.title}</p>
                <p className="sbx-action-why">{a.why}</p>
                <div className="sbx-action-foot" style={{ justifyContent: "flex-end" }}>
                  <button type="button" onClick={a.onAction}>
                    {a.evidence}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="sbx-empty">No hay acciones operacionales pendientes derivadas para este período.</div>
        )}
      </Section>

    </div>
  );
}
